import { useState } from "react";
import api, { formatError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const CFG = {
  "materias-primas": { singular: "materia prima", campos: ["unidad"] },
  productos: { singular: "producto", campos: ["unidad"] },
  proveedores: { singular: "proveedor", campos: ["cif"] },
  clientes: { singular: "cliente", campos: [] },
};

// Select de maestro con opción "+ Crear …" inline: nadie se topa con un desplegable
// vacío ni con un 404. El maestro se crea en el momento en que hace falta.
export default function QuickMaestroSelect({ tipo, value, onChange, items = [], onCreated, proveedores = [], onProveedorCreated, placeholder = "Seleccione…", testid, disabled }) {
  const cfg = CFG[tipo] || { singular: tipo, campos: [] };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nombre: "", unidad: "kg", cif: "", proveedor_id: "" });
  const [busy, setBusy] = useState(false);

  const crear = async () => {
    if (!form.nombre.trim()) return toast.error("El nombre es obligatorio");
    setBusy(true);
    try {
      const payload = { nombre: form.nombre.trim() };
      if (cfg.campos.includes("unidad")) payload.unidad = form.unidad || "kg";
      if (cfg.campos.includes("cif") && form.cif.trim()) payload.cif = form.cif.trim();
      if (tipo === "materias-primas" || tipo === "productos") payload.alergenos = [];
      if (tipo === "materias-primas" && form.proveedor_id) payload.proveedor_id = form.proveedor_id;
      const { data } = await api.post(`/${tipo}`, payload);
      toast.success(`${cfg.singular.charAt(0).toUpperCase() + cfg.singular.slice(1)} creado`);
      onCreated?.(data);
      onChange?.(data.id);
      setOpen(false);
      setForm({ nombre: "", unidad: "kg", cif: "", proveedor_id: "" });
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Select value={value || ""} disabled={disabled}
        onValueChange={(v) => (v === "__create__" ? setOpen(true) : onChange?.(v))}>
        <SelectTrigger data-testid={testid} className="mt-1.5"><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent className="bg-white">
          {items.length === 0 && (
            <div className="px-2 py-2 text-xs text-[#0E2A47]/40">Aún no hay {cfg.singular}s. Crea la primera abajo 👇</div>
          )}
          {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.nombre}</SelectItem>)}
          <SelectItem value="__create__" className="text-[#FF5625] font-semibold" data-testid={`${testid}-crear-option`}>
            + Crear {cfg.singular}
          </SelectItem>
        </SelectContent>
      </Select>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid={`${testid}-crear-dialog`}>
          <DialogHeader>
            <DialogTitle className="text-[#0E2A47]">Nueva {cfg.singular}</DialogTitle>
            <DialogDescription className="text-[#0E2A47]/60 text-sm">Da de alta {cfg.singular === "proveedor" || cfg.singular === "producto" ? "el" : "la"} {cfg.singular} sin salir de esta pantalla. Se seleccionará automáticamente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Nombre</Label>
              <Input autoFocus value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") crear(); }}
                data-testid={`${testid}-crear-nombre`} className="mt-1.5" />
            </div>
            {cfg.campos.includes("unidad") && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Unidad</Label>
                <Select value={form.unidad} onValueChange={(v) => setForm({ ...form, unidad: v })}>
                  <SelectTrigger data-testid={`${testid}-crear-unidad`} className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="kg">kg</SelectItem><SelectItem value="g">g</SelectItem>
                    <SelectItem value="l">l</SelectItem><SelectItem value="ud">ud</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {cfg.campos.includes("cif") && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">CIF (opcional)</Label>
                <Input value={form.cif} onChange={(e) => setForm({ ...form, cif: e.target.value })} data-testid={`${testid}-crear-cif`} className="mt-1.5" />
              </div>
            )}
            {tipo === "materias-primas" && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Proveedor (opcional)</Label>
                <QuickMaestroSelect tipo="proveedores" value={form.proveedor_id}
                  onChange={(v) => setForm({ ...form, proveedor_id: v })}
                  items={proveedores} onCreated={(p) => onProveedorCreated?.(p)}
                  testid={`${testid}-crear-proveedor`} placeholder="Sin proveedor · o crear uno" />
                <p className="text-xs text-[#0E2A47]/40 mt-1">Vincula el proveedor ahora para que la materia prima no nazca huérfana.</p>
              </div>
            )}
            <p className="text-xs text-[#0E2A47]/50">Puedes completar el resto de datos más tarde en <b>Maestros</b>.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid={`${testid}-crear-cancelar`}>Cancelar</Button>
            <Button onClick={crear} disabled={busy} data-testid={`${testid}-crear-submit`} className="bg-[#FF5625] hover:bg-[#E0451A]">
              {busy ? "Creando…" : "Crear y seleccionar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
